import axios from 'axios';
import {
  Application,
  ApplicationDetails,
  DisplayedProduct,
  SearchedProductName,
  SearchedProductNameUpdate,
  ETIMClass, // <-- Добавляем импорт ETIMClass
  ETIMFeature, // <-- Добавляем импорт ETIMFeature
  FilterSortParameters,
  User, // <-- Добавляем импорт User
  TelegramData, // <-- Добавляем импорт TelegramData
  CategoryBrandMapping,
  BrandReselectionRequest,
  BrandReselectionResponse,
  PDResultResponse,
  PipelineResult,
  SignedUrlsResponse,
  UserBrandPreference, // <-- Добавляем импорт UserBrandPreference
  WebSearchResult,
  PublicProductSearchResponse,
  CncMatchTrace,
  CncCandidateSelectionResponse
} from '../types/models';
import { API_ENDPOINTS } from '../config/api-config';
import { webSocketService } from './websocket'; // Импортируем сервис WebSocket

// Создаем axios-инстанс с общими настройками
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000', // URL для разработки
  headers: {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true' // Обход предупреждения ngrok
  },
  withCredentials: true // <--- Добавить эту строку
});

console.log('[ApiService] baseURL:', apiClient.defaults.baseURL);
console.log('[ApiService] REACT_APP_API_BASE_URL:', process.env.REACT_APP_API_BASE_URL);
console.log('[ApiService] REACT_APP_ENVIRONMENT:', process.env.REACT_APP_ENVIRONMENT);

export class ApiService {
  private static getApiErrorMessage(error: unknown, fallback: string): string {
    if (axios.isAxiosError(error)) {
      const data = error.response?.data;
      const detail = data?.detail;
      if (typeof detail === 'string' && detail.trim()) {
        return detail;
      }
      if (detail && typeof detail === 'object') {
        const detailMessage = (detail as { message?: unknown }).message;
        if (typeof detailMessage === 'string' && detailMessage.trim()) {
          return detailMessage;
        }
      }
      const message = data?.message;
      if (typeof message === 'string' && message.trim()) {
        return message;
      }
    }
    return fallback;
  }

  // Вспомогательный метод для обеспечения подключения к WebSocket
  private static async ensureWebSocketConnection(userId: number): Promise<void> {
    try {
      console.log('Ensuring WebSocket is connected...');
      await webSocketService.connect(userId);
      console.log('WebSocket is ready.');
    } catch (error) {
      console.error('Failed to ensure WebSocket connection:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      throw new Error(`WebSocket connection failed: ${errorMessage}`);
    }
  }

  // Получение списка заявок
  static async getApplications(): Promise<Application[]> {
    const response = await apiClient.get<Application[]>('/api/applications/my');
    return response.data;
  }

  // Получение данных заявки
  static async getApplication(appId: number): Promise<ApplicationDetails> {
    const response = await apiClient.get<ApplicationDetails>(`/api/applications/${appId}`);
    const applicationData = response.data;
    if (applicationData && applicationData.products) {
      applicationData.products = applicationData.products.map((spn, index) => ({
        ...spn,
        orderNumber: index + 1,
      }));
    }
    return applicationData; // Возвращаем модифицированные данные
  }

  // Получение статуса обработки заявки
  static async getApplicationStatus(appId: number): Promise<{ processing_status: string | null }> {
    const response = await apiClient.get<{ processing_status: string | null }>(`/api/applications/${appId}/status`);
    return response.data;
  }

  // Получение одного SPN
  static async getSPN(spnId: number): Promise<SearchedProductName> {
    const response = await apiClient.get<SearchedProductName>(`/api/spn/${spnId}`);
    return response.data;
  }

  static async getCncMatchTrace(spnId: number): Promise<CncMatchTrace | null> {
    const response = await apiClient.get<CncMatchTrace | null>(`/api/spn/${spnId}/cnc-match-trace`);
    return response.data;
  }

  static async selectCncCandidate(spnId: number, cncProductId: number): Promise<CncCandidateSelectionResponse> {
    try {
      const response = await apiClient.put<CncCandidateSelectionResponse>(`/api/spn/${spnId}/cnc-candidate`, {
        cnc_product_id: cncProductId,
      });
      return response.data;
    } catch (error) {
      throw new Error(this.getApiErrorMessage(error, 'Не удалось выбрать CNC-аналог'));
    }
  }

  // Получение вариантов продуктов для SPN
  static async getSPNProducts(spnId: number, brand?: string | null): Promise<DisplayedProduct[]> {
    const params: any = {};
    if (brand) {
      params.brand = brand;
    }
    const response = await apiClient.get<DisplayedProduct[]>(`/api/spn/${spnId}/products`, { params });
    return response.data;
  }

  // Обновление имени SPN
  static async updateSPNName(spnId: number, userId: number, update: SearchedProductNameUpdate): Promise<SearchedProductName> {
    // 1. Убедиться, что WebSocket подключен или инициировать подключение.
    try {
      console.log('Ensuring WebSocket is connected before updating SPN name...');
      await this.ensureWebSocketConnection(userId);
      console.log('WebSocket is ready for SPN name update process.');
    } catch (error) {
      console.error('Failed to ensure WebSocket connection for SPN name update:', error);
      throw error; // Пробрасываем ошибку дальше
    }

    // 2. Отправить HTTP-запрос на бэкенд для обновления имени SPN.
    // Этот запрос инициирует поиск на бэкенде, который затем будет отправлять обновления через WebSocket.
    try {
      console.log('Updating SPN name via HTTP with data:', update);
      const response = await apiClient.put<SearchedProductName>(`/api/spn/${spnId}/name?user_id=${userId}`, update);
      // Ответ от HTTP-запроса может содержать начальное состояние SPN.
      return response.data;
    } catch (error) {
      console.error('Error updating SPN name via HTTP:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      throw new Error(`Failed to update SPN name via HTTP: ${errorMessage}`);
    }
  }

  // Выбор продукта для SPN
  static async selectProduct(spnId: number, productCode: string): Promise<SearchedProductName> {
    try {
      const response = await apiClient.put<SearchedProductName>(
        `/api/spn/${spnId}/select-product/${productCode}`
      );
      return response.data;
    } catch (error) {
      console.error('Error selecting product:', error);
      throw new Error('Failed to select product');
    }
  }

  // Отмена выбора продукта для SPN
  static async deselectProduct(spnId: number): Promise<SearchedProductName> {
    try {
      const response = await apiClient.delete<SearchedProductName>(
        `/api/spn/${spnId}/selected-product`
      );
      return response.data;
    } catch (error) {
      console.error('Error deselecting product:', error);
      throw new Error('Failed to deselect product');
    }
  }

  // Выбор продукта для SPN по артикулу
  static async selectProductByVendorCode(spnId: number, vendorCode: string): Promise<DisplayedProduct[]> {
    try {
      const response = await apiClient.post<DisplayedProduct[]>(
        `/api/spn/${spnId}/select-product-by-vendor-code`,
        { vendor_code: vendorCode } // Передаем vendor_code в теле запроса
      );
      return response.data;
    } catch (error) {
      console.error(`Error selecting product by vendor code for SPN ${spnId} with vendor code ${vendorCode}:`, error);
      throw new Error('Failed to select product by vendor code');
    }
  }

  // Получение списка уникальных брендов для списка SPN
  static async getBrandsForSpns(spnIds: number[]): Promise<string[]> {
    try {
      const response = await apiClient.post<string[]>(
        '/api/spn/brands',
        { spn_ids: spnIds }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching brands for SPNs:', error);
      throw new Error('Failed to fetch brands for SPNs');
    }
  }

  // Получение брендов для категории
  static async getBrandsForCategory(categoryId: number): Promise<string[]> {
    try {
      const response = await apiClient.get<string[]>(`/api/categories/${categoryId}/brands`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching brands for category ${categoryId}:`, error);
      // Возвращаем пустой массив или пробрасываем ошибку в зависимости от нужной логики
      return [];
      // throw new Error(`Failed to fetch brands for category ${categoryId}`);
    }
  }

  // Получение характеристик для категории
  static async getCategoryFeatures(categoryId: number): Promise<ETIMFeature[]> {
    try {
      const response = await apiClient.get<ETIMFeature[]>(`/api/categories/${categoryId}/features`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching category features for category ${categoryId}:`, error);
      // Возвращаем пустой массив или пробрасываем ошибку в зависимости от нужной логики
      return [];
      // throw new Error(`Failed to fetch category features for category ${categoryId}`);
    }
  }

  static async updateSpnFeatures(spnId: number, features: ETIMFeature[], filterSortParams?: FilterSortParameters): Promise<SearchedProductName> {
    try {
      // Тело запроса соответствует схеме EtimUpdatePayload на бэкенде
      const payload: { etim_features: ETIMFeature[]; filter_sort_params?: FilterSortParameters } = {
        etim_features: features
      };
      if (filterSortParams) {
        payload.filter_sort_params = filterSortParams;
      }
      const response = await apiClient.put<SearchedProductName>(`/api/spn/${spnId}/etim`, payload); // Эндпоинт остается прежним
      return response.data;
    } catch (error) {
      console.error(`Error updating features for SPN ${spnId}:`, error);
      // Пробрасываем ошибку дальше, чтобы ее можно было обработать в компоненте
      throw new Error(`Failed to update features for SPN ${spnId}`);
    }
  }

  // Проверка статуса аутентификации через бэкенд
  static async checkAuthStatus(): Promise<{ user: User | null }> {
    try {
      // Этот эндпоинт на бэкенде должен проверить HttpOnly cookie
      const response = await apiClient.get('/api/auth/status');
      // Если запрос успешен, бэкенд вернул данные пользователя, включая ID и tg_data
      const { userId, tg_data } = response.data;
      const user: User = {
        id: userId,
        tg_id: tg_data?.id, // Предполагаем, что tg_data содержит id
        tg_name: tg_data?.username || tg_data?.first_name, // Используем username или first_name
        tg_data: tg_data as TelegramData // Приводим к типу TelegramData
      };
      return { user: user };
    } catch (error) {
      // Если бэкенд вернул 401 Unauthorized или другую ошибку, пользователь не аутентифицирован
      console.error('Authentication check failed:', error);
      return { user: null };
    }
  }

  // Получение деталей продукта по коду
  static async getProductDetails(productCode: string): Promise<any> { // TODO: Определить более точный тип ответа
    try {
      const response = await apiClient.get(`/api/products/${productCode}/details`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching product details for ${productCode}:`, error);
      throw new Error(`Failed to fetch product details for ${productCode}`);
    }
  }

  // Получение списка заявок текущего пользователя
  static async getMyApplications(): Promise<Application[]> {
    try {
      const response = await apiClient.get<Application[]>('/api/applications/my');
      return response.data;
    } catch (error) {
      console.error('Error fetching user applications:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      throw new Error(`Failed to fetch user applications: ${errorMessage}`);
    }
  }

  // Загрузка файла для создания заявки с указанием типа
  static async uploadFile(file: File, fileType: string, userId: number): Promise<any> {
    // 1. Убедиться, что WebSocket подключен перед загрузкой файла
    try {
      console.log('Ensuring WebSocket is connected before uploading file...');
      await this.ensureWebSocketConnection(userId);
      console.log('WebSocket is ready for file upload process.');
    } catch (error) {
      console.error('Failed to ensure WebSocket connection for file upload:', error);
      throw error; // Пробрасываем ошибку дальше
    }

    // 2. Отправить файл на бэкенд
    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_type', fileType);

    try {
      const response = await apiClient.post('/api/uploadfile', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading file:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      throw new Error(`Failed to upload file: ${errorMessage}`);
    }
  }

  // Загрузка файла определенного типа с созданием заявки
  static async uploadFileWithType(file: File, fileType: 'specification' | 'electrical_scheme' | 'technical_doc', userId: number): Promise<any> {
    console.log(`ApiService.uploadFileWithType called with fileType: ${fileType}, file: ${file.name}, userId: ${userId}`); // Добавляем логирование
    
    // 1. Убедиться, что WebSocket подключен перед загрузкой файла
    try {
      console.log('Ensuring WebSocket is connected before uploading file...');
      await this.ensureWebSocketConnection(userId);
      console.log('WebSocket is ready for file upload process.');
    } catch (error) {
      console.error('Failed to ensure WebSocket connection for file upload:', error);
      throw error; // Пробрасываем ошибку дальше
    }

    // 2. Отправить файл на бэкенд
    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_type', fileType);
    
    console.log(`Sending formData with file_type: ${fileType}`); // Добавляем логирование

    try {
      const response = await apiClient.post('/api/uploadfile', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      console.log('Upload response:', response.data); // Добавляем логирование ответа
      return response.data;
    } catch (error) {
      console.error('Error uploading file:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      throw new Error(`Failed to upload file: ${errorMessage}`);
    }
  }

  // Получить отчет заявки
  static async getApplicationReport(applicationId: number): Promise<{report: string}> {
    try {
      const response = await apiClient.get(`/api/applications/${applicationId}/report`);
      return response.data;
    } catch (error) {
      console.error('Error fetching application report:', error);
      throw new Error('Failed to fetch application report');
    }
  }

  // Обновить отчет заявки
  static async updateApplicationReport(applicationId: number, reportContent: string): Promise<{message: string}> {
    try {
      const response = await apiClient.put(`/api/applications/${applicationId}/report`, {
        report: reportContent
      });
      return response.data;
    } catch (error) {
      console.error('Error updating application report:', error);
      throw new Error('Failed to update application report');
    }
  }

  // Запустить обработку отчета для подбора оборудования
  static async processApplicationReport(applicationId: number, userId: number): Promise<{message: string}> {
    // 1. Убедиться, что WebSocket подключен перед запуском обработки отчета
    try {
      console.log('Ensuring WebSocket is connected before processing report...');
      await this.ensureWebSocketConnection(userId);
      console.log('WebSocket is ready for report processing.');
    } catch (error) {
      console.error('Failed to ensure WebSocket connection for report processing:', error);
      throw error; // Пробрасываем ошибку дальше
    }

    // 2. Отправить запрос на обработку отчета
    try {
      console.log(`Processing report for application ${applicationId} via HTTP.`);
      const response = await apiClient.post(`/api/applications/${applicationId}/process-report`);
      return response.data;
    } catch (error) {
      console.error('Error processing application report:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      throw new Error(`Failed to process application report: ${errorMessage}`);
    }
  }

  static async triggerWebSearch(spnId: number): Promise<SearchedProductName> {
    try {
      const response = await apiClient.post(`/api/spn/${spnId}/web-search`);
      return response.data;
    } catch (error) {
      console.error(`Error triggering web search for SPN ${spnId}:`, error);
      throw new Error(this.getApiErrorMessage(error, 'Не удалось запустить веб-поиск'));
    }
  }

  static async publicProductSearch(query: string, brand?: string): Promise<PublicProductSearchResponse> {
    const response = await apiClient.post<PublicProductSearchResponse>('/api/public/search', {
      query,
      brand: brand || null,
    });
    return response.data;
  }

  static async selectWebSearchResult(spnId: number, payload: WebSearchResult): Promise<SearchedProductName> {
    try {
      const response = await apiClient.post<SearchedProductName>(
        `/api/searched_product_names/${spnId}/web-search/select`,
        payload
      );
      return response.data;
    } catch (error) {
      console.error(`Error applying web search selection for SPN ${spnId}:`, error);
      throw new Error('Не удалось применить выбранный результат веб-поиска');
    }
  }

  static async determineCategoryForSpn(spnId: number, userId: number): Promise<SearchedProductName> {
    try {
      console.log('Ensuring WebSocket is connected before determining category...');
      await this.ensureWebSocketConnection(userId);
      console.log('WebSocket is ready for category determination process.');
    } catch (error) {
      console.error('Failed to ensure WebSocket connection for category determination:', error);
      throw error; // Пробрасываем ошибку дальше
    }

    try {
      console.log(`Determining category for SPN ${spnId} via HTTP.`);
      const response = await apiClient.post<SearchedProductName>(`/api/spn/${spnId}/determine-category`);
      return response.data;
    } catch (error) {
      console.error(`Error determining category for SPN ${spnId}:`, error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      throw new Error(`Failed to determine category for SPN ${spnId}: ${errorMessage}`);
    }
  }

  // Обновление заявки (включая комментарий)
  static async updateApplication(application: ApplicationDetails): Promise<ApplicationDetails> {
    try {
      const response = await apiClient.put<ApplicationDetails>(`/api/applications/`, application);
      return response.data;
    } catch (error) {
      console.error(`Error updating application ${application.id}:`, error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      throw new Error(`Failed to update application: ${errorMessage}`);
    }
  }

  // Обновление только комментария заявки (не затирает другие поля)
  static async updateApplicationComment(applicationId: number, comment: string): Promise<Application> {
    try {
      const response = await apiClient.patch<Application>(`/api/applications/${applicationId}/comment`, { comment });
      return response.data;
    } catch (error) {
      console.error(`Error updating application comment ${applicationId}:`, error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      throw new Error(`Failed to update application comment: ${errorMessage}`);
    }
  }

  // Email авторизация - проверка существования email
  static async checkEmailExists(email: string): Promise<{ exists: boolean; requirePassword: boolean }> {
    try {
      const response = await apiClient.post('/api/auth/email-check', { email }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Email check failed:', error);
      throw new Error('Ошибка при проверке email');
    }
  }

  // Email регистрация (для новых пользователей)
  static async registerWithEmail(email: string, nextUrl: string = '/my-applications'): Promise<void> {
    try {
      const formData = new FormData();
      formData.append('email', email);
      formData.append('next_url', nextUrl);
      
      // Этот запрос вернет redirect, поэтому мы просто делаем POST
      await apiClient.post('/api/auth/email-register', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    } catch (error) {
      console.error('Email registration failed:', error);
      throw new Error('Ошибка при регистрации');
    }
  }

  // Email вход (для существующих пользователей)
  static async loginWithEmail(email: string, password: string, nextUrl: string = '/my-applications'): Promise<void> {
    try {
      const formData = new FormData();
      formData.append('email', email);
      formData.append('password', password);
      formData.append('next_url', nextUrl);
      
      // Этот запрос вернет redirect, поэтому мы просто делаем POST
      await apiClient.post('/api/auth/email-login', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    } catch (error) {
      console.error('Email login failed:', error);
      throw new Error('Неверный email или пароль');
    }
  }

  // Переподбор брендов
  static async startBrandReselection(
    applicationId: number, 
    categoryBrandMappings: { category3_id: number; brand_name: string }[]
  ): Promise<{ status: string; message: string; application_id: number }> {
    try {
      const response = await apiClient.post(
        `/api/applications/${applicationId}/brand-reselection`,
        { category_brand_mappings: categoryBrandMappings }
      );
      return response.data;
    } catch (error) {
      console.error('Brand reselection failed:', error);
      throw new Error('Не удалось запустить переподбор брендов');
    }
  }

  // ============ PD Pipeline Result Methods ============

  // Получить результат PD pipeline для заявки
  static async getPDResult(applicationId: number): Promise<PDResultResponse> {
    try {
      const response = await apiClient.get(`/api/applications/${applicationId}/pd-result`);
      return response.data;
    } catch (error) {
      console.error('Error fetching PD result:', error);
      throw new Error('Failed to fetch PD result');
    }
  }

  // Обновить результат PD pipeline
  static async updatePDResult(applicationId: number, pdResult: PipelineResult): Promise<{message: string; pd_result: PipelineResult}> {
    try {
      const response = await apiClient.put(`/api/applications/${applicationId}/pd-result`, {
        pd_result: pdResult
      });
      return response.data;
    } catch (error) {
      console.error('Error updating PD result:', error);
      throw new Error('Failed to update PD result');
    }
  }

  // Получить подписанные URL для GCS файлов
  static async getSignedUrls(gcsUris: string[]): Promise<SignedUrlsResponse> {
    console.log('[ApiService.getSignedUrls] START');
    console.log('[ApiService.getSignedUrls] gcsUris:', gcsUris);
    console.log('[ApiService.getSignedUrls] Request payload:', { gcs_uris: gcsUris });
    
    try {
      const response = await apiClient.post('/api/signed-urls', {
        gcs_uris: gcsUris
      });
      console.log('[ApiService.getSignedUrls] Response received:', response);
      console.log('[ApiService.getSignedUrls] Response data:', response.data);
      return response.data;
    } catch (error) {
      console.error('[ApiService.getSignedUrls] ERROR:', error);
      console.error('[ApiService.getSignedUrls] Error details:', error instanceof Error ? error.message : 'Unknown error');
      if ((error as any).response) {
        console.error('[ApiService.getSignedUrls] Response status:', (error as any).response.status);
        console.error('[ApiService.getSignedUrls] Response data:', (error as any).response.data);
      }
      throw new Error('Failed to get signed URLs');
    }
  }

  // Отправить обрезанные PDF страницы на обработку
  static async processSchemePageCrops(
    applicationId: number,
    pageNumber: number,
    formData: FormData
  ): Promise<{ message: string }> {
    try {
      const response = await apiClient.post(
        `/api/applications/${applicationId}/schemes/${pageNumber}/process-crops`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      console.log('Crop processing request accepted:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error processing scheme page crops:', error);
      throw new Error('Failed to process scheme page crops');
    }
  }

    // Запустить подбор оборудования по результатам PD pipeline
  static async runProductSelection(
    applicationId: number,
    userId: number
  ): Promise<{ success: boolean; message: string }> {
    // 1. Убедиться, что WebSocket подключен перед запуском подбора
    try {
      console.log('Ensuring WebSocket is connected before running product selection...');
      await this.ensureWebSocketConnection(userId);
      console.log('WebSocket is ready for product selection process.');
    } catch (error) {
      console.error('Failed to ensure WebSocket connection for product selection:', error);
      throw error; // Пробрасываем ошибку дальше
    }

    // 2. Отправить запрос на запуск подбора оборудования
    try {
      console.log(`Running product selection for application ${applicationId} via HTTP.`);
      const response = await apiClient.post(
        `/api/applications/${applicationId}/run_product_selection`
      );
      return response.data;
    } catch (error) {
      console.error('Error running product selection:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      throw new Error(`Failed to run product selection: ${errorMessage}`);
    }
  }

  // Обновить количество SPN (без запуска поиска)
  static async updateSPNQuantity(spnId: number, quantity: number): Promise<SearchedProductName> {
    try {
      const response = await apiClient.put<SearchedProductName>(
        `/api/spn/${spnId}/quantity`,
        { quantity }
      );
      return response.data;
    } catch (error) {
      console.error(`Error updating SPN quantity for ${spnId}:`, error);
      throw new Error('Failed to update SPN quantity');
    }
  }

  // ============ User Brand Preferences Methods ============

  // Сохранить предпочтения пользователя по брендам
  static async saveUserBrandPreferences(
    preferences: Array<{ level3_category_id: number; brand_name: string }>
  ): Promise<UserBrandPreference[]> {
    try {
      const response = await apiClient.post<UserBrandPreference[]>(
        '/api/user-brand-preferences',
        preferences
      );
      return response.data;
    } catch (error) {
      console.error('Error saving user brand preferences:', error);
      throw new Error('Не удалось сохранить предпочтения по брендам');
    }
  }

  // Получить предпочтения пользователя по брендам
  static async getUserBrandPreferences(): Promise<UserBrandPreference[]> {
    try {
      const response = await apiClient.get<UserBrandPreference[]>(
        '/api/user-brand-preferences'
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching user brand preferences:', error);
      throw new Error('Не удалось получить предпочтения по брендам');
    }
  }

  // Удалить предпочтение для категории
  static async deleteUserBrandPreference(categoryId: number): Promise<void> {
    try {
      await apiClient.delete(`/api/user-brand-preferences/${categoryId}`);
    } catch (error) {
      console.error(`Error deleting user brand preference for category ${categoryId}:`, error);
      throw new Error('Не удалось удалить предпочтение по бренду');
    }
  }
}
